import * as React from 'react';
import globalEvents = require('../../util/globalEvents');
import ImageInlineEditor from '../../image-inline-editor/components/ImageInlineEditor';
import IIEPayload from '../../image-inline-editor/interfaces/Payload';
import IieDefaultPayload from '../../image-inline-editor/core/DefaultPayload';
import {ImageModuleBaseData, BaseViewProps, ImageViewProps, ImageWithTextViewProps} from './interfaces';
import {getIieSourceImageUrl} from './util';
import {setCurrentTab} from '../../module-action-bar/index';
import AppRegistry from '../../AppRegistry';
import SyncPosition, {PositionBox} from '../../components/SyncPosition';

import diyLightTheme = require("../../material-ui/diyLightTheme");
import Popover from 'material-ui/Popover';
import Menu from 'material-ui/Menu';
import MenuItem from 'material-ui/MenuItem';
import * as common from './common';
import l10n from '../../util/l10n';
import IconAdd from 'material-ui/svg-icons/image/add-a-photo';
import IconLink from 'material-ui/svg-icons/editor/insert-link';
import IconResetSize from 'material-ui/svg-icons/notification/sync';

function getStore() {
    return AppRegistry.getStoreProvider().getStore();
}

interface ImageInPlaceEditViewState {
    sourceImage: HTMLImageElement;
    useShallowCompare: boolean;
    contextMenuOpen: boolean;
    contextMenuTop: number;
    contextMenuLeft: number;
}

export default class ImageInPlaceEditView extends React.Component<BaseViewProps, ImageInPlaceEditViewState> {
    static childContextTypes: React.ValidationMap<any> = {
        muiTheme: React.PropTypes.object
    };
    private panUsed = false;
    private aspectCropUsed = false;
    private nonAspectCropUsed = false;

    getChildContext() {
        return {
            muiTheme: diyLightTheme
        };
    }

    constructor(props: BaseViewProps) {
        super(props);
        this.state = {
            sourceImage: null,
            useShallowCompare: false,
            contextMenuOpen: false,
            contextMenuTop: 0,
            contextMenuLeft: 0
        };
    }

    componentDidMount() {
        this.loadSourceImage();
    }

    componentWillReceiveProps(nextProps: BaseViewProps) {
        if (getIieSourceImageUrl(this.props.payload) !== getIieSourceImageUrl(nextProps.payload)) {
            this.loadSourceImage();
        }
    }

    render() {
        if (this.state.sourceImage) {
            return this.renderLoaded();
        } else {
            // TODO render a loader or render with a transparent image
            return null;
        }
    }

    renderLoaded() {
        const element = this.props.module.getImageElement();
        const position = this.elementFixed(element) ? 'fixed' : 'absolute';
        return (
            <SyncPosition useShallowCompare={this.state.useShallowCompare} element={element} position={position}>
            {this.renderIie}
            </SyncPosition>
        );
    }

    private renderIie = (box: PositionBox) => {
        const data = this.props.payload;
        const imageElement = this.props.module.getImageElement(data.imageId);
        const imageElPosition = this.elementFixed(imageElement) ? "fixed" : "absolute";
        const resizeControls = this.getResizeControls();
        return (
            <div onContextMenu={this.handleContextMenu} onTouchTap={this.handleTouchTap}>
                <ImageInlineEditor
                    top={box.top}
                    left={box.left}
                    width={box.width}
                    height={box.height}
                    imageElement={imageElement}
                    resizeControls={resizeControls}
                    maxWidth={this.props.module.getMaxWidth()}
                    sourceImage={this.state.sourceImage}
                    payload={data.iiePayload || IieDefaultPayload}
                    position={imageElPosition}
                    zIndex={10000}
                    mediaType={data.imageMediaType}
                    onPan={this.onPan}
                    onPanStart={this.onPanStart}
                    onPanEnd={this.onPanEnd}
                    onCropResize={this.onCropResize}
                    onCropResizeStart={this.onCropResizeStart}
                    onCropResizeEnd={this.onCropResizeEnd}
                    showSourceImage={data.showSourceImage}
                    animatedGif={data.imageAnimated}
                    outline={data.allowCrop ? 2 : 1}
                    allowPan={data.allowPan}
                />

                <Popover
                    style={{marginLeft: this.state.contextMenuLeft, marginTop: this.state.contextMenuTop}}
                    open={this.state.contextMenuOpen}
                    useLayerForClickAway={true}
                    autoCloseWhenOffScreen={true}
                    canAutoPosition={true}
                    onRequestClose={this.handleContextMenuClose}
                    zDepth={2}
                >
                    <div onContextMenu={(e) => {e.preventDefault(); e.stopPropagation();}}>
                        <Menu>
                            <MenuItem
                                primaryText={l10n('module.image.buttons.addNew')}
                                leftIcon={<IconAdd />}
                                onTouchTap={this.handleSelectImage}
                            />
                            <MenuItem
                                primaryText={l10n('module.image.buttons.link')}
                                leftIcon={<IconLink />}
                                onTouchTap={this.handleAddLink}
                            />
                            {
                            resizeControls && resizeControls.length > 0 && !this.isFullWidth() ?
                            <MenuItem
                                primaryText={l10n('module.image.buttons.resetSize')}
                                leftIcon={<IconResetSize />}
                                onTouchTap={this.handleResetSize}
                            />
                            : null
                            }
                        </Menu>
                    </div>
                </Popover>
            </div>
        );
    };

    private handleSelectImage = () => {
        this.handleContextMenuClose();
        common.handleSelectImage(
            this.props.moduleId,
            this.props.module,
            this.props.payload,
            this.onDataChange
        );
    };

    private handleAddLink = () => {
        this.handleContextMenuClose();
        common.handleAddLink(this.props.payload, this.onDataChange);
    };

    private handleResetSize = () => {
        this.handleContextMenuClose();
        common.handleResetSize(this.props.payload, this.props.module, this.onDataChange);
    };

    private onDataChange = (newData: ImageModuleBaseData) => {
        this.props.onDataChange(newData);
    };

    private handleContextMenu = (e: React.MouseEvent<{}>) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({
            contextMenuOpen: true,
            contextMenuLeft: e.clientX,
            contextMenuTop: e.clientY
        } as ImageInPlaceEditViewState);
    };

    private handleTouchTap = () => {
        const store = getStore();
        store.dispatch(setCurrentTab({nextTab: 0, currentTabWhitelist: [3, 4]}));
    };

    private handleContextMenuClose = (reason?: string) => {
        this.setState({contextMenuOpen: false} as ImageInPlaceEditViewState);
    };

    private loadSourceImage() {
        this.props.module.getIieSourceImage().then((image: HTMLImageElement) => {
            const state = {} as ImageInPlaceEditViewState;
            state.sourceImage = image;
            this.setState(state);
        });
    }

    private hasImageProps(props: BaseViewProps): props is ImageViewProps {
        return !props.module.hasText;
    }

    private hasTextProps(props: BaseViewProps): props is ImageWithTextViewProps {
        return props.module.hasText;
    }

    private isFullWidth() {
        const props = this.props as BaseViewProps;
        return this.hasImageProps(props) && props.payload.isFullwidth;
    }

    private onCropResize = (
        imageWidth: number,
        imageHeight: number,
        payload: IIEPayload,
        imageDataUrlGetter: () => string,
        keepAspect: boolean
    ) => {
        const maxWidth = this.props.module.getMaxWidth();
        const newData = {} as ImageModuleBaseData;
        newData.imageWidth = imageWidth;
        newData.imageHeight = imageHeight;
        newData.visualImageWidth = imageWidth;
        newData.visualImageHeight = imageHeight;
        newData.imageFit = imageWidth >= maxWidth;
        newData.iiePayload = payload;

        // we don't need to update imageUrl for performance reasons
        // we have the original image covered by ImageInlineEditor anyway
        //newData.imageUrl = imageDataUrlGetter();
        this.props.onDataChange(newData);

        if (keepAspect) {
            if (!this.aspectCropUsed) {
                this.aspectCropUsed = true;
                globalEvents.trigger('diy-editor-iie-crop-used-keep-aspect');
            }
        } else {
            if (!this.nonAspectCropUsed) {
                this.nonAspectCropUsed = true;
                globalEvents.trigger('diy-editor-iie-crop-used-non-aspect');
            }
        }
    };

    private onPan = (payload: IIEPayload) => {
        const newData = {} as ImageModuleBaseData;
        newData.iiePayload = payload;
        this.props.onDataChange(newData);
    };

    private onPanStart = () => {
        if (!this.panUsed) {
            this.panUsed = true;
            globalEvents.trigger('diy-editor-iie-panning-used');
        }
        const newData = {} as ImageModuleBaseData;
        newData.showSourceImage = true;
        this.props.onDataChange(newData);
    };

    private onPanEnd = () => {
        const newData = {} as ImageModuleBaseData;
        newData.showSourceImage = false;
        this.props.onDataChange(newData);
    };

    private onCropResizeStart = () => {
        const newData = {} as ImageModuleBaseData;
        newData.showSourceImage = this.props.payload.allowCrop;
        this.props.onDataChange(newData);

        const state = {} as ImageInPlaceEditViewState;
        state.useShallowCompare = true;
        this.setState(state);

        // auto switch tabs if needed
        const store = getStore();
        store.dispatch(setCurrentTab({nextTab: 0, currentTabWhitelist: [3, 4]}));
    };

    private onCropResizeEnd = () => {
        this.aspectCropUsed = false;
        this.nonAspectCropUsed = false;

        const newData = {} as ImageModuleBaseData;
        newData.showSourceImage = false;
        this.props.onDataChange(newData);

        const state = {} as ImageInPlaceEditViewState;
        state.useShallowCompare = false;
        this.setState(state);
    };

    private getResizeControls() {
        const props = this.props as BaseViewProps;
        const hasResize =
            (this.hasImageProps(props) && props.payload.supports.imageResize)
            || this.hasTextProps(props);

        if (!hasResize) {
            return [];
        }

        const module = this.props.module;
        const data = this.props.payload;
        const pos = data.imagePosition;
        const fullwidth = this.isFullWidth();
        const matrix = module.getMatrixModuleParent();
        const teaser = matrix && matrix.isTeaser();
        const animated = data.imageAnimated;
        let controls: string[];

        if (fullwidth) {
            controls = animated ? [] : ['S'];
        } else if (teaser) {
            controls = animated ? [] : ['S'];
        } else if (pos === 1) {
            controls = animated ? ['SE'] : ['S', 'SE', 'E'];
        } else if (pos === 2) {
            controls = animated ? ['SW'] : ['S', 'SW', 'W'];
        } else if (pos === 3) {
            controls = animated ? ['SE', 'SW'] : ['S', 'SE', 'E', 'SW', 'W'];
        } else {
            controls = [];
        }

        if (!data.allowCrop) {
            controls = controls.filter((value) => ['E', 'S', 'W'].indexOf(value) === -1);
        }

        return controls;
    }

    private elementFixed(element: HTMLElement) {
        let current = element;
        while (current) {
            if (window.getComputedStyle(current).position === "fixed") {
                return true;
            }
            current = current.offsetParent as HTMLElement;
        }
        return false;
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/image/InPlaceEditView.tsx
 **/